import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatRadioChange } from '@angular/material/radio';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';

import {
  COMPLEX_FILTER_SERVICE,
  LogicalQueryFormGroupValue,
} from '@blaze-runtime/engine-ui-feature';
import {
  AggregateFunctionName,
  ColumnDataType,
  ColumnLinkInfo,
  DisplayInfo,
  DurationColumnFormat,
  FullDatasetInfo,
  LogicalQueryNode,
  LogicalType,
  NumberPrecision,
  ProjectInfo,
  QueryNodeType,
  RelatedDatasetLink,
  RollupColumnInfo,
  SignatureColumnType,
  SystemTableType,
  TagItemInfo,
} from '@blaze-shared/api-contracts-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import {
  DatasetsApiService,
  DatasetsCollectionService,
  InlineColumnResponse,
  RelatedApiService,
  RelatedDataset,
} from '@blaze-shared/datasets-data-access';
import {
  BlazeTable,
  BlazeTableActionType,
  convertDatasetRefSchemasToBlazeTables,
} from '@blaze-shared/datasets-domain';
import {
  CURRENCY_OPTIONS,
  DEPRECATED_DURATION_FORMAT_MAPPINGS,
} from '@blaze-shared/elements-domain';
import {
  INTELLI_FUNCTIONS_SCOPE_TOKEN,
  IntelliConfig,
  IntellisenseError,
  IntelliValueInfo,
  InvalidFormulaIntellisenseError,
} from '@blaze-shared/intellisense-feature';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { compareAscBy } from '@blaze-shared/platform-util';
import { ProjectsCollectionService } from '@blaze-shared/projects-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BaseSchema,
  ColumnType,
  DatasetColumn,
  DATE_DEFAULT_VALUE,
  DATETIME_DEFAULT_VALUE,
  DateTimeColumnFormat,
  DEFAULT_ID_COL,
  DEFAULT_WF_SCHEMA,
  isLinkedInlineColumn,
  NumberColumnFormat,
  TextColumnFormat,
} from '@blaze-shared/schema-domain';
import { BlazeAutocompleteComponent, safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  formulaFloorRegex,
  getCultureByCountry,
  isEqualNoUndefined,
  LOCALES,
  regexpMatch,
  SelectOption,
  SelectOptionWithData,
} from '@blaze-shared/shared-util';
import { deepClone, isNullish, type Nullish, NUMBER_PRECISIONS } from '@blaze-shared/shared-util';

import {
  DEFAULT_TAG,
  TemporaryTagItemInfo,
} from '../column-tags-editor-section/column-tags-editor-section.component';
import {
  DatasetColumnExtendedForLinkEdit,
  DatasetLinkExtendedForLinkEdit,
} from '../shared/blaze-table.model';
import { BlazeTableService } from '../shared/blaze-table.service';
import { BlazeTableStore } from '../shared/blaze-table.store';
import {
  findMatchingInlineColumns,
  isInlineColumnReadonlyLink,
} from '../shared/utils/column-type-link.utils';

import { RollupComplexFilterService } from './complex-filter.service';

interface FieldTypeOption {
  columnDataType: ColumnDataType | undefined;
  columnType: ColumnType;
  icon: string;
  isProfilePicture?: boolean;
  viewValue: string;
}

type DropdownSelection<T> = T | null | undefined | (T | null | undefined)[];

const FIELD_TYPES: Partial<Record<ColumnType, FieldTypeOption>> = {
  String: {
    icon: 'icon_field_type_text_16',
    columnDataType: 'String',
    columnType: 'String',
    viewValue: 'Text',
  },
  Number: {
    icon: 'icon_field_type_number_16',
    columnDataType: 'Number',
    columnType: 'Number',
    viewValue: 'Number',
  },
  Email: {
    icon: 'icon_field_type_email_16',
    columnDataType: 'String',
    columnType: 'Email',
    viewValue: 'Email',
  },
  PhoneNumber: {
    icon: 'icon_field_type_phone_16',
    columnDataType: 'String',
    columnType: 'PhoneNumber',
    viewValue: 'Phone',
  },
  DateTime: {
    icon: 'icon_field_input_datetime_24',
    columnDataType: 'DateTime',
    columnType: 'DateTime',
    viewValue: 'Date & Time',
  },
  Time: {
    icon: 'icon_field_input_datetime_24',
    columnDataType: 'Time',
    columnType: 'Time',
    viewValue: 'Time of Day',
  },
  Duration: {
    icon: 'icon_field_type_duration_16',
    columnDataType: 'Duration',
    columnType: 'Duration',
    viewValue: 'Duration',
  },
  Boolean: {
    icon: 'icon_field_type_bool_16',
    columnDataType: 'Boolean',
    columnType: 'Boolean',
    viewValue: 'Check',
  },
  File: {
    icon: 'icon_field_type_file_16',
    columnDataType: 'File',
    columnType: 'File',
    viewValue: 'File',
  },
  Image: {
    icon: 'icon_field_type_image_16',
    columnDataType: 'Image',
    columnType: 'Image',
    viewValue: 'Image',
  },
  Signature: {
    icon: 'icon_field_type_signature_16',
    columnDataType: 'Signature',
    columnType: 'Signature',
    viewValue: 'Signature',
  },
  SecureText: {
    icon: 'icon_field_type_secure_text_16',
    columnDataType: 'String',
    columnType: 'SecureText',
    viewValue: 'Secure Text',
  },
  Url: {
    icon: 'icon_field_type_url_16',
    columnDataType: 'String',
    columnType: 'Url',
    viewValue: 'URL',
  },
  Link: {
    icon: 'icon_field_type_link_16',
    columnDataType: 'Link',
    columnType: 'Link',
    viewValue: 'Linked Field',
  },
  Tag: {
    icon: 'icon_field_type_tag_16',
    columnDataType: 'Tag',
    columnType: 'Tag',
    viewValue: 'Options',
  },
  Formula: {
    icon: 'icon_field_type_formula_16',
    columnDataType: undefined,
    columnType: 'Formula',
    viewValue: 'Formula',
  },
  AccessRole: {
    icon: 'icon_field_type_access_role_16',
    columnDataType: 'Tag',
    columnType: 'AccessRole',
    viewValue: 'Access Role',
  },
  ProfilePicture: {
    icon: 'icon_field_type_image_16',
    columnDataType: 'Image',
    columnType: 'ProfilePicture',
    isProfilePicture: true,
    viewValue: 'Profile Picture',
  },
  CreatedTime: {
    icon: 'icon_field_type_date_16',
    columnDataType: undefined,
    columnType: 'CreatedTime',
    viewValue: 'Created Time',
  },
  LastUpdatedTime: {
    icon: 'icon_field_type_date_16',
    columnDataType: undefined,
    columnType: 'LastUpdatedTime',
    viewValue: 'Updated Time',
  },
  AutoNumber: {
    icon: 'icon_field_type_number_16',
    columnDataType: undefined,
    columnType: 'AutoNumber',
    viewValue: 'Autonumber',
  },
  CreatedBy: {
    icon: 'icon_field_type_text_16',
    columnDataType: undefined,
    columnType: 'CreatedBy',
    viewValue: 'Created By',
  },
  UpdatedBy: {
    icon: 'icon_field_type_text_16',
    columnDataType: undefined,
    columnType: 'UpdatedBy',
    viewValue: 'Updated By',
  },
  Rollup: {
    icon: 'icon_field_type_rollup_16',
    columnDataType: undefined,
    columnType: 'Rollup',
    viewValue: 'Rollup',
  },
  TimeZone: {
    icon: 'icon_field_input_datetime_24',
    columnDataType: 'String',
    columnType: 'TimeZone',
    viewValue: 'Time Zone',
  },
  ZipCode: {
    icon: 'icon_field_type_text_16',
    columnDataType: 'String',
    columnType: 'ZipCode',
    viewValue: 'Zip Code',
  },
  GeoPoint: {
    icon: 'icon_field_type_location_16',
    columnDataType: 'GeoPoint',
    columnType: 'GeoPoint',
    viewValue: 'Map Coordinates',
  },
};

const NUMBER_FIELD_FORMATS: SelectOption<NumberColumnFormat | null>[] = [
  { value: null, label: 'Default' },
  { value: 'Currency', label: 'Currency' },
  { value: 'Percent', label: 'Percent' },
];

const TEXT_FIELD_FORMATS: SelectOption<TextColumnFormat | null>[] = [
  { value: 'SingleLine', label: 'Single Line' },
  { value: 'Paragraph', label: 'Paragraph' },
  { value: 'RichText', label: 'Rich Text' },
];

const DURATION_FIELD_FORMATS: SelectOption<DurationColumnFormat | null>[] = [
  { value: 'h:mm', label: 'h:mm' },
  { value: 'h:mm:ss', label: 'h:mm:ss' },
  { value: 'h:mm:ss.s', label: 'h:mm:ss.s' },
  { value: 'd h:mm', label: 'd h:mm' },
  { value: 'd h:mm:ss', label: 'd h:mm:ss' },
];

const SIGNATURE_FIELD_OPTIONS: SelectOption<SignatureColumnType | null>[] = [
  { value: 'Handwritten', label: 'Handwritten' },
  { value: 'Typed', label: 'Typing' },
];

const DATETIME_FIELD_FORMATS: SelectOption<DateTimeColumnFormat | null>[] = [
  { value: 'DateTime', label: 'Date & Time' },
  { value: 'Date', label: 'Date' },
];

const DATE_DEFAULT_VALUES: SelectOption<string>[] = [
  { value: '', label: 'None' },
  { value: DATE_DEFAULT_VALUE, label: 'Today' },
];

const DATETIME_DEFAULT_VALUES: SelectOption<string>[] = [
  { value: '', label: 'None' },
  { value: DATETIME_DEFAULT_VALUE, label: 'Now' },
];

const COLUMN_TYPES_WITH_FORMAT = new Set<ColumnType>([
  'Duration',
  'Email',
  'Formula',
  'Number',
  'PhoneNumber',
  'SecureText',
  'String',
  'TimeZone',
  'Url',
  'ZipCode',
]);

@UntilDestroy()
@Component({
  selector: 'blaze-column-editor-panel',
  templateUrl: './column-editor-panel.component.html',
  styleUrls: ['./column-editor-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    RollupComplexFilterService,
    {
      provide: COMPLEX_FILTER_SERVICE,
      useExisting: RollupComplexFilterService,
    },
    { provide: INTELLI_FUNCTIONS_SCOPE_TOKEN, useValue: 'BlazeTable' },
  ],
})
export class ColumnEditorPanelComponent implements OnInit, AfterViewInit {
  private readonly blazeTableService = inject(BlazeTableService);
  private readonly blazeTableStore = inject(BlazeTableStore);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly datasetsCollService = inject(DatasetsCollectionService);
  private readonly notificationService = inject(NotificationService);
  private readonly projectsCollService = inject(ProjectsCollectionService);
  private readonly relatedApiService = inject(RelatedApiService);
  private readonly rollupComplexFilterService = inject(RollupComplexFilterService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject<Store<any>>(Store);

  @Input() tableId: string | undefined;
  @Input() tableConfig$!: Observable<BlazeTable | undefined>;
  @Input() rowsCount!: number;
  @Input() allowedConversions!: Partial<Record<ColumnType, ColumnType[]>>;
  @Output() closePanel = new EventEmitter<void>();
  @Output() addField = new EventEmitter<DatasetColumn>();
  @ViewChild('host', { read: ElementRef }) host!: ElementRef<HTMLElement>;
  @ViewChild('fieldTypeMenuTrigger') fieldTypeMenuTrigger!: MatMenuTrigger;
  @ViewChild('fieldFormatMenuTrigger') fieldFormatMenuTrigger!: MatMenuTrigger;
  @ViewChild('currencyMenuTrigger') currencyMenuTrigger!: MatMenuTrigger;
  @ViewChild('numberPrecisionMenuTrigger') numberPrecisionMenuTrigger!: MatMenuTrigger;
  @ViewChild('fieldDatasetMenuTrigger') fieldDatasetMenuTrigger!: MatMenuTrigger;
  @ViewChild('nameField') nameField?: ElementRef<HTMLInputElement>;

  private tableConfig!: BlazeTable;
  private actionType!: BlazeTableActionType;
  // column props
  private readonly fieldType$$ = new BehaviorSubject<FieldTypeOption | undefined>(undefined);
  private readonly datasetInlineColumns$$ = new BehaviorSubject<string>('');
  private readonly datasets$$ = new BehaviorSubject<BlazeTable[]>([]);
  private readonly formulaError$$ = new BehaviorSubject<any>(undefined);
  private fieldTypeValue!: FieldTypeOption | undefined;
  private canAddAccessRoleColumn = false;
  private canAddProfilePictureColumn = false;

  protected linkedTableCtrl = new FormControl<string | null>(null); // dataset id
  protected fieldTypeCtrl = new FormControl<string | null>(null);
  protected fieldTypeCtrlValue = toSignal(this.fieldTypeCtrl.valueChanges);
  protected fieldTypeCtrlPristine = toSignal(
    this.fieldTypeCtrl.statusChanges.pipe(map(() => this.fieldTypeCtrl.pristine)),
  );

  protected filteredFieldTypes = computed<SelectOptionWithData<FieldTypeOption>[]>(() => {
    const searchTerm = this.fieldTypeCtrlValue()?.toLowerCase();
    const isPristine = this.fieldTypeCtrlPristine();
    if (!searchTerm || isPristine) {
      return this.optionsFieldTypes();
    }
    return this.optionsFieldTypes().filter((option) =>
      option.label.toLowerCase().includes(searchTerm),
    );
  });

  allowDynamicValues = false;
  currency = 'USD';
  dateTimeDefaultValue = '';
  numberDefaultValue: 'NONE' | null = null;
  dateTimeFieldOption: DateTimeColumnFormat | null = null;
  defaultValue?: any;
  displayInfos: DisplayInfo[] = [];
  durationFieldFormat: DurationColumnFormat | null = null;
  fieldName = '';
  formatCulture = '';
  formula = '';
  initialUpdatedColumn: DatasetColumn | null = null;
  isMultipleValues = false;
  isRequired = false;
  isSecure = false;
  isUnique = false;
  link!: ColumnLinkInfo | null; // Chosen link
  numberFieldFormat: NumberColumnFormat | null = null;
  numberPrecision: NumberPrecision;
  rollupDefinition: RollupColumnInfo = {
    tableId: '',
    columnId: undefined,
    aggregateFunctionName: 'NONE',
    linkColumnId: undefined,
    query: {
      nodeType: QueryNodeType.Logical,
      logicalType: LogicalType.And,
      children: [],
    } satisfies LogicalQueryNode,
  };
  signatureFieldOption: SignatureColumnType | null = null;
  sourceColumn: DatasetColumn | null = null;
  tags: TemporaryTagItemInfo[] = [];
  textFieldFormat: TextColumnFormat | null = null;
  updatedColumn: DatasetColumn | null = null;

  // column peer props
  fieldTypes: FieldTypeOption[] = [];
  numberFieldFormats: SelectOption<NumberColumnFormat | null>[] = [];
  textFieldFormats: SelectOption<TextColumnFormat | null>[] = [];
  durationFieldFormats: SelectOption<DurationColumnFormat | null>[] = [];
  dateTimeFieldOptions: SelectOption<DateTimeColumnFormat | null>[] = [];
  dateTimeDefaultValues: SelectOption<string>[] = DATETIME_DEFAULT_VALUES;
  dateDefaultValues: SelectOption<string>[] = DATE_DEFAULT_VALUES;
  currencies = CURRENCY_OPTIONS;
  allCountriesLocale = LOCALES.map((value) => ({ value: value.locale, label: value.country }));
  numberPrecisions: SelectOption<NumberPrecision>[];
  dataset!: BlazeTable | null; // Chosen dataset
  datasetInlineColumns!: (InlineColumnResponse & { icon: string; selected: boolean })[];
  inlineColumns: Partial<DatasetColumn>[] = [];
  signatureFieldOptions: SelectOption<SignatureColumnType | null>[] = [];

  projectId = '';
  title = '';
  saveButtonLabel = '';
  intelliConfig$!: Observable<IntelliConfig>;
  noValidColumnsFound = false;
  optionsDatasets$ = this.datasets$$.pipe(
    map((datasets) => {
      this.verifySourceColumnTableLinked(datasets);
      return datasets.map<SelectOptionWithData<BlazeTable>>((dataset) => {
        const projectName =
          dataset.projectId === this.tableConfig.projectId ? '' : dataset.projectName;
        const data = {
          ...dataset,
          projectName,
        };
        return {
          value: dataset.id,
          label: '',
          data,
        };
      });
    }),
  );
  optionsFieldTypes = signal<SelectOptionWithData<FieldTypeOption>[]>([]);

  rollupConfigTableId$: Observable<SelectOptionWithData<RelatedDataset>[]>;
  rollupConfigTableColumns: SelectOptionWithData<DatasetColumn, string>[] = [];
  rollupConfigColumnIds: SelectOptionWithData<RelatedDatasetLink, string>[] = [];
  readonly aggregationFunctions: SelectOption<AggregateFunctionName>[] = [
    { label: 'AVERAGE', value: 'AVG' },
    { label: 'COUNT ROWS', value: 'COUNT' },
    { label: 'MAX', value: 'MAX' },
    { label: 'MEDIAN', value: 'MEDIAN' },
    { label: 'MIN', value: 'MIN' },
    { label: 'SUM', value: 'SUM' },
  ];
  rollupFiltersEnabled = false;

  emptyValuesInColumn = false;

  set fieldType(fieldTypeOption: FieldTypeOption | undefined) {
    this.fieldTypeValue = fieldTypeOption;
    this.fieldType$$.next(this.fieldTypeValue);
  }
  get fieldType(): FieldTypeOption | undefined {
    return this.isLinkEditView
      ? this.fieldTypes.find((f) => f.columnType === 'Link')
      : this.fieldTypeValue;
  }

  get showMultiValueDataLossNotification(): boolean {
    // if was a multi value column previously and now is not
    return (
      this.actionType === BlazeTableActionType.Edit &&
      wasMultipleValue(this.sourceColumn) &&
      this.isMultipleValues === false
    );
  }

  get isAddAction(): boolean {
    return [
      BlazeTableActionType.Add,
      BlazeTableActionType.InsertLeft,
      BlazeTableActionType.InsertRight,
    ].includes(this.actionType);
  }

  get isAddActionAndHasRows(): boolean {
    return this.isAddAction && this.rowsCount > 0;
  }

  get isLinkInfoVisible(): boolean {
    return !!(
      this.dataset?.connectorName &&
      this.sourceColumn?.link?.columnName &&
      this.actionType === BlazeTableActionType.Edit &&
      this.fieldType?.columnDataType === 'Link'
    );
  }

  get isColumnDefinitionValid(): boolean {
    if (
      !this.tableConfig ||
      !this.fieldType ||
      !this.blazeTableService.validateColumnName(
        this.fieldName,
        this.sourceColumn,
        this.tableConfig.columns,
      ).isValid ||
      (this.showRequiredAdditionalText && isNullish(this.defaultValue))
    ) {
      return false;
    }
    const { columnDataType, columnType } = this.fieldType;
    return !!(
      (columnDataType !== 'Link' &&
        columnDataType !== 'Number' &&
        columnDataType !== 'Tag' &&
        columnType !== 'ZipCode' &&
        columnType !== 'Formula') ||
      (columnDataType === 'Link' && this.link?.columnName && this.link?.tableId) ||
      (columnDataType === 'Number' && this.numberFieldFormat !== 'Currency') ||
      (columnDataType === 'Number' && this.numberFieldFormat === 'Currency' && this.currency) ||
      (columnType === 'Formula' && this.formula) ||
      (columnDataType === 'Tag' && this.tags?.length) ||
      (columnType === 'ZipCode' && this.formatCulture)
    );
  }

  get isLinkEditView(): boolean {
    return this.actionType === BlazeTableActionType.Edit && this.isFormulaForLinkEditView;
  }

  get isFormulaForLinkEditView(): boolean {
    return this.sourceColumn?.columnType === 'Formula' && isLinkedInlineColumn(this.sourceColumn);
  }

  get showDefaultValueForTags(): boolean {
    return !!(
      (this.fieldType?.columnDataType === 'Tag' && this.tags.length) ||
      this.fieldType?.columnDataType !== 'Tag'
    );
  }

  get allowNoneForCountryFormat(): boolean {
    return this.fieldType?.columnType === 'PhoneNumber';
  }

  get placeholderForCountryFormat(): string {
    return this.allowNoneForCountryFormat ? 'Select country (optional)' : 'Select country';
  }

  get defaultValuePlaceholder(): string {
    switch (this.fieldType?.columnType) {
      case 'GeoPoint':
        return 'Lat°, Long°';
      default:
        return '';
    }
  }

  get dateTimeDefaultValuesOptions(): SelectOption<string, string>[] {
    return this.dateTimeFieldOption === 'Date' ? DATE_DEFAULT_VALUES : DATETIME_DEFAULT_VALUES;
  }

  get showRequiredAdditionalText(): boolean {
    return (this.emptyValuesInColumn && this.updatedColumn?.isRequired) || false;
  }

  get isSourceInlineLink(): boolean {
    return !!this.sourceColumn && isLinkedInlineColumn(this.sourceColumn);
  }

  get isLinkToDisabled(): boolean {
    return !!(
      (this.sourceColumn?.columnType === 'Link' && this.actionType === BlazeTableActionType.Edit) ||
      this.isSourceInlineLink
    );
  }

  constructor() {
    this.linkedTableCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((datasetId) => {
      const datasetItem = this.datasets$$.value.find((ds) => ds.id === datasetId);
      if (datasetItem && this.dataset?.id !== datasetItem.id) {
        this.showColumnsOrSelectDefault(datasetItem, true);
      }
      this.verifySourceColumnTableLinked(this.datasets$$.value);
    });

    this.fieldTypeCtrl.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(() => this.fieldTypeCtrl.dirty),
      )
      .subscribe((value) => {
        const item = this.optionsFieldTypes().find((o) => o.label === value)?.data;
        if (!item) {
          return;
        }
        this.fieldTypeCtrl.markAsPristine();
        this.fieldTypeCtrl.setValue(item.viewValue, { emitEvent: false });
        this.onIsSecureChanged(item.columnType === 'SecureText');
        this.setFieldType(undefined, item);
      });

    this.numberPrecisions = NUMBER_PRECISIONS.filter(
      (numberPrecision) => numberPrecision !== null,
    ).map((numberPrecision) => ({
      value: numberPrecision,
      label: numberPrecision === null ? 'None' : (1).toFixed(numberPrecision),
    }));
    this.numberPrecision = this.numberPrecisions[0].value;
  }

  ngOnInit(): void {
    this.store
      .select(fromRouter.getProjectId)
      .pipe(take(1))
      .subscribe((projectId) => {
        this.projectId = projectId;
      });

    const datasetIds = [this.tableId].filter(Boolean) as string[];
    this.rollupConfigTableId$ = this.relatedApiService
      .fetchRelated({
        datasetIds,
        projectId: this.projectId,
        addIndirect: false,
        includeAllLinks: true,
      })
      .pipe(
        map((records) => {
          const results: SelectOptionWithData<RelatedDataset>[] = records
            .filter(
              (r) =>
                r.sourceDatasetId === this.tableId &&
                r.links.filter((r) => r.isReverseLink)?.length,
            )
            .map((r) => ({
              value: r.id,
              label: r.name,
              data: r,
            }));
          return results || [];
        }),
        shareReplay(1),
      );

    // Pull data for possible field edit.
    combineLatest([this.blazeTableStore.sourceColumn$, this.blazeTableStore.actionType$])
      .pipe(
        untilDestroyed(this),
        filter(([sourceColumn, actionType]) => !!sourceColumn && !!actionType),
      )
      .subscribe(([sourceColumn, actionType]) => {
        this.title = 'Add New Field';
        this.saveButtonLabel = 'Add';
        this.sourceColumn = deepClone(sourceColumn);
        this.link = sourceColumn?.link || null;
        this.actionType = actionType;
        if (this.sourceColumn && actionType === BlazeTableActionType.Edit) {
          // Edit Type
          this.setDefaultsForActionEdit(this.sourceColumn);
          this.setFieldType(undefined, this.fieldType);
        } else if (
          this.sourceColumn &&
          (actionType === BlazeTableActionType.Add ||
            actionType === BlazeTableActionType.InsertLeft ||
            actionType === BlazeTableActionType.InsertRight)
        ) {
          // Add new column
          this.sourceColumn.name = '';
          this.sourceColumn.formula = '';
          this.sourceColumn.displayInfos = [];
          this.sourceColumn.dataType = undefined;
          this.sourceColumn.link = undefined;
          this.setDefaultsForActionAdd();
        } else {
          this.setDefaultsForActionAdd();
        }
        this.updateColumn();
        this.verifyColumn(this.updatedColumn);
        this.initialUpdatedColumn = deepClone(this.updatedColumn);
        this.setPendingChanges();
      });

    this.tableConfig$
      .pipe(
        filter((tableConfig) => !!tableConfig),
        tap((tableConfig) => {
          this.tableConfig = tableConfig;
        }),
        map((tableConfig) => ({
          canAddAccessRoleColumn:
            !tableConfig?.columns.find(isAccessRoleColumn) &&
            tableConfig.id === this.schemaService.usersSchema?.id,
          canAddProfilePictureColumn:
            !tableConfig?.columns.find(isProfilePictureColumn) &&
            tableConfig.systemTableType === SystemTableType.InternalUsers,
        })),
        distinctUntilChanged(),
      )
      .subscribe(({ canAddAccessRoleColumn, canAddProfilePictureColumn }) => {
        this.canAddAccessRoleColumn = canAddAccessRoleColumn;
        this.canAddProfilePictureColumn = canAddProfilePictureColumn;
        this.setFieldTypes();
        safeDetectChanges(this.cdr);
      });

    this.datasetInlineColumns$$
      .pipe(
        untilDestroyed(this),
        switchMap((datasetId: string) =>
          datasetId
            ? this.datasetsApiService.getInlineColumns(datasetId, this.tableConfig.id)
            : EMPTY,
        ),
        tap(() => {
          const sourceColumn =
            this.sourceColumn?.columnType === 'Link'
              ? this.sourceColumn
              : this.isLinkEditView
                ? this.tableConfig.columns.find(
                    (c) => c.id && this.sourceColumn?.formula?.includes(c.id),
                  )
                : undefined;

          this.inlineColumns =
            this.tableConfig.columns.filter(
              (c) => c.columnType === 'Formula' && c.formula?.includes(sourceColumn?.id as string),
            ) || [];
        }),
        map((columns) =>
          columns.map((column) => {
            let icon =
              Object.values(FIELD_TYPES).find((type) => type.columnType === column.columnType)
                ?.icon || '';
            if (['Date', 'DateTime', 'Time'].includes(column.columnType)) {
              switch (column.columnType) {
                case 'Date':
                  icon = 'icon_field_type_date_16';
                  break;
                case 'DateTime':
                case 'Time':
                  icon = 'icon_field_input_datetime_24';
                  break;
              }
            }
            return {
              ...column,
              icon,
              selected:
                !!this.inlineColumns?.find((c) => {
                  if (!c.formula || !c.formula.includes(column.id)) {
                    return false;
                  }
                  return (
                    (isInlineColumnReadonlyLink(c) && column.isLink) ||
                    (!isInlineColumnReadonlyLink(c) && !column.isLink)
                  );
                }) || false,
            };
          }),
        ),
      )
      .subscribe((columns) => {
        // We need to check if we need to duplicate record when linked column had a display col change
        const indexToDuplicate = columns.findIndex((col) => col.isDisplayColumn && col.selected);
        if (indexToDuplicate > -1) {
          columns.splice(indexToDuplicate, 0, deepClone(columns[indexToDuplicate]));
          columns[indexToDuplicate].selected = false;
          columns[indexToDuplicate + 1].isDisplayColumn = false;
        }
        this.datasetInlineColumns = columns;
        safeDetectChanges(this.cdr);
      });

    this.tableConfig$
      .pipe(
        filter((v) => !!v),
        first(),
        switchMap((tableConfig) =>
          this.sourceColumn?.name
            ? this.blazeTableService.checkIfEmptyValuesInColumn(tableConfig, this.sourceColumn.name)
            : of(this.rowsCount > 0),
        ),
        untilDestroyed(this),
      )
      .subscribe((emptyRows) => {
        this.emptyValuesInColumn = emptyRows;
        safeDetectChanges(this.cdr);
      });
  }

  ngAfterViewInit(): void {
    // We need to read size of the popup. For this reason, we wait until the popup is fully rendered
    // before reading its width and creating config.
    setTimeout(() => {
      this.intelliConfig$ = this.createIntelliSenseConfig$();
    });
    this.nameField?.nativeElement.focus();
  }

  processColumnName(): void {
    const result = this.blazeTableService.validateColumnName(
      this.fieldName,
      this.sourceColumn,
      this.tableConfig.columns,
    );
    if (!result.isValid && result.error.code !== 'NOT_UNIQUE') {
      this.notificationService.error(result.error.message);
    } else if (!result.isValid) {
      this.fieldName = this.blazeTableService.normalizeColumnName(
        this.fieldName,
        this.tableConfig.columns,
      );
    }
    this.verifyColumn(this.updatedColumn);
    this.updateColumn(false);
    this.cdr.detectChanges();
  }

  onFieldTypeFocus(
    autocomplete: BlazeAutocompleteComponent<string>,
    fieldInput: HTMLInputElement,
  ): void {
    autocomplete.isOpen = true;
    fieldInput.select();
  }

  onFieldTypeBlur(): void {
    this.fieldTypeCtrl.markAsPristine();
    if (
      !this.optionsFieldTypes().some((o) => o.label === this.fieldTypeCtrl.value) &&
      this.fieldType
    ) {
      this.fieldTypeCtrl.setValue(this.fieldType.viewValue, { emitEvent: false });
    }
  }

  onTextFormatChanged(value: MatRadioChange): void {
    this.textFieldFormat = value.value;
    this.updateColumn();
  }

  onDateTimeFormatChanged(value: MatRadioChange): void {
    this.dateTimeFieldOption = value.value;
    this.defaultValue = '';
    this.updateColumn();
  }

  onDateTimeDefaultValueChanged(value: MatRadioChange): void {
    this.dateTimeDefaultValue = value.value;
    this.defaultValue = this.dateTimeDefaultValue;
    this.updateColumn();
  }

  onNumberDefaultValueChanged(value: MatRadioChange): void {
    this.numberDefaultValue = value.value;
    this.setDefaultValue(value.value);
    this.updateColumn();
  }

  onCultureFormatChanged(culture: string | string[]): void {
    this.formatCulture = (Array.isArray(culture) ? culture[0] : culture) || '';
    this.updateColumn();
  }

  onIsSecureChanged(isSecure: boolean): void {
    this.isSecure = isSecure;
    this.updateColumn();
  }

  onNumberPrecisionChanged(numberPrecision: NumberPrecision | NumberPrecision[]): void {
    this.numberPrecisionMenuTrigger?.closeMenu();
    this.numberPrecision = Array.isArray(numberPrecision) ? numberPrecision[0] : numberPrecision;
    this.updateColumn();
  }

  onSignatureOptionChanged(value: MatRadioChange): void {
    this.signatureFieldOption = value.value;
    this.updateColumn();
  }

  onRollupSourceTableChange(
    value: string | null | (string | null)[],
    tables: SelectOptionWithData<RelatedDataset>[] | null,
  ): void {
    const relatedRecord = tables?.find((tab) => tab.value === value)?.data;
    if (!relatedRecord) {
      this.rollupConfigTableColumns = [];
      this.rollupConfigColumnIds = [];
      return;
    }
    this.rollupDefinition.tableId = relatedRecord.id;
    this.setRollupConfigLinkedColumns(relatedRecord);
    this.setRollupConfigTableColumns(relatedRecord.id);

    if (relatedRecord.links.length === 1 && !this.rollupDefinition.linkColumnId) {
      this.rollupDefinition.linkColumnId = relatedRecord.links[0].columnId;
    } else if (
      !this.rollupConfigColumnIds?.find(
        (c) => c.data.columnId === this.rollupDefinition.linkColumnId,
      )
    ) {
      this.rollupDefinition.linkColumnId = '';
    }
    safeDetectChanges(this.cdr);
    this.updateColumn();
  }

  onRollupSourceColumnChange(
    value: DropdownSelection<string>,
    links: SelectOptionWithData<RelatedDatasetLink, string>[],
  ): void {
    const relatedRecord = links?.find((tab) => tab.value === value)?.data;
    if (!relatedRecord) {
      return;
    }
    this.rollupDefinition.linkColumnId = relatedRecord.columnId ?? '';
    safeDetectChanges(this.cdr);
    this.updateColumn();
  }

  onRollupAggregationFunctionChange(value: DropdownSelection<AggregateFunctionName>): void {
    if (Array.isArray(value)) {
      value = value[0];
    }
    if (!value) {
      return;
    }
    this.rollupDefinition.aggregateFunctionName = value;
    this.updateColumn();
  }

  onRollupColumnChange(value: DropdownSelection<string>): void {
    this.rollupDefinition.columnId = this.rollupConfigTableColumns.find(
      (col) => col.data.id === value,
    )?.data.id;
    this.updateColumn();
  }

  queryChange(node: { query: LogicalQueryFormGroupValue }): void {
    const root: LogicalQueryNode = {
      nodeType: QueryNodeType.Logical,
      children: [...node.query.children],
      logicalType: node.query.logicalType,
    };

    this.rollupDefinition.query = root;
  }

  close(): void {
    this.resetPanel();
    this.closePanel.emit();
  }

  saveCurrentField(): void {
    this.updateColumn(false);
    // When it's Formula we need to validate it
    if (
      this.tableId &&
      this.updatedColumn?.columnType === 'Formula' &&
      !isLinkedInlineColumn(this.updatedColumn)
    ) {
      this.validateFormula(this.updatedColumn, this.tableId).subscribe({
        next: (_res) => {
          if (this.updatedColumn) {
            this.addField.emit(this.updatedColumn);
          }
          this.resetPanel();
        },
        error: (err: any) => {
          this.notificationService.error(err.error.errorMessage);
          this.formulaError$$.next(err.error.errorMessage);
        },
      });
    } else {
      if (this.updatedColumn) {
        this.addField.emit(this.fixLinkedColumnTypes(this.updatedColumn));
      }
      this.resetPanel();
    }
  }

  //#region properties setters

  setTags(tags: TemporaryTagItemInfo[]): void {
    this.tags = tags;
    this.updateColumn();
  }

  setDefaultValue(value: any): void {
    if (value && Array.isArray(value) && !value.length) {
      this.defaultValue = null;
    } else {
      this.defaultValue = value !== '' ? value : null;
    }
    this.updateColumn();
  }

  setFieldType(event: MouseEvent | undefined, fieldType: FieldTypeOption | undefined): void {
    event?.stopPropagation();
    if (this.fieldType?.columnDataType !== fieldType?.columnDataType) {
      this.defaultValue = null;
    }
    this.fieldType = fieldType;
    this.fieldTypeCtrl.markAsPristine();
    this.fieldTypeCtrl.setValue(fieldType?.viewValue || null, { emitEvent: false });
    this.fieldTypeMenuTrigger?.closeMenu();
    if (fieldType?.columnDataType === 'Link') {
      if (this.actionType === BlazeTableActionType.Edit) {
        this.showLinkedColumn();
      } else {
        this.showDatasets();
      }
    } else {
      this.link = null;
    }
    if (
      this.actionType === BlazeTableActionType.Edit &&
      fieldType?.columnDataType === 'Tag' &&
      this.sourceColumn &&
      this.sourceColumn.columnType !== 'Tag' &&
      this.tableConfig?.id &&
      this.sourceColumn?.id
    ) {
      this.datasetsApiService
        .getUniqueTagsForColum(this.tableConfig.id, this.sourceColumn.id)
        .pipe(untilDestroyed(this))
        .subscribe((res) => {
          this.tags = res.options.map((tag) => ({
            ...DEFAULT_TAG,
            id: crypto.randomUUID(),
            value: tag,
          }));
          if (res.multiple) {
            this.isMultipleValues = true;
          }
          this.updateColumn();
        });
    } else {
      this.updateColumn();
    }
    if (
      this.updatedColumn?.format?.formatType === 'ZipCode' &&
      !this.updatedColumn.format.culture
    ) {
      this.onCultureFormatChanged('en-US');
    } else if (this.updatedColumn?.format?.formatType !== 'ZipCode') {
      this.onCultureFormatChanged(this.sourceColumn?.format?.culture || '');
    }
    safeDetectChanges(this.cdr);
  }

  setFieldFormat(fieldFormat: NumberColumnFormat | null | (NumberColumnFormat | null)[]): void {
    if (Array.isArray(fieldFormat)) {
      fieldFormat = fieldFormat[0];
    }

    this.fieldFormatMenuTrigger?.closeMenu();

    if (this.numberFieldFormat === fieldFormat || this.numberFieldFormat === fieldFormat) {
      return;
    }

    if (this.numberFieldFormat === 'Percent' && !fieldFormat) {
      const numberPrecision = (this.numberPrecision || 0) + 2;
      this.numberPrecision = numberPrecision > 8 ? 8 : (numberPrecision as NumberPrecision);
    } else if (fieldFormat === 'Percent' && !this.numberFieldFormat) {
      const numberPrecision = (this.numberPrecision || 0) - 2;
      this.numberPrecision = numberPrecision > -1 ? (numberPrecision as NumberPrecision) : 0;
    }

    this.numberFieldFormat = !Array.isArray(fieldFormat) ? fieldFormat : fieldFormat[0];
    this.currency = 'USD';

    this.updateColumn();
    safeDetectChanges(this.cdr);
  }

  setDurationFormat(format: DurationColumnFormat | null | (DurationColumnFormat | null)[]): void {
    if (Array.isArray(format)) {
      format = format[0];
    }

    this.durationFieldFormat = format || null;

    this.updateColumn();
    safeDetectChanges(this.cdr);
  }

  setCurrency(currency: string | string[]): void {
    this.currencyMenuTrigger?.closeMenu();

    this.currency = !Array.isArray(currency) ? currency : currency[0];
    this.updateColumn();
  }

  setFormulaValue(value: IntelliValueInfo): void {
    this.displayInfos = value.displayInfos || [];
    this.formula = value.value || '';
    this.updateColumn();
    this.verifyColumn(this.updatedColumn);
  }

  setIsUnique(event: MatSlideToggleChange): void {
    this.isUnique = event.checked;
    if (this.isUnique) {
      this.isRequired = true;
    }
    this.updateColumn();
  }

  setIsRequired(event: MatSlideToggleChange): void {
    this.isRequired = event.checked;
    this.updateColumn();
  }

  setIsMultipleValues(event: MatSlideToggleChange): void {
    this.isMultipleValues = event.checked;

    if (this.isMultipleValues && !Array.isArray(this.defaultValue)) {
      this.defaultValue = this.defaultValue ? [this.defaultValue] : [];
    } else if (!this.isMultipleValues && Array.isArray(this.defaultValue)) {
      this.defaultValue = this.defaultValue.length ? this.defaultValue[0] : null;
    }
    this.updateColumn();
  }

  setAllowDynamicValues(event: MatSlideToggleChange): void {
    this.allowDynamicValues = event.checked;

    this.updateColumn();
  }

  setLinkDatasetColumn(col: DatasetColumn): void {
    this.link = {
      columnName: col.name,
      dataType: col.dataType,
      tableId: this.dataset?.id || '',
      displayColumn: this.dataset?.displayColumn || '',
    };
    this.updateColumn();
  }

  setInlineColumn(column: InlineColumnResponse): void {
    if (column.isDisplayColumn) {
      return;
    }

    const selectedIndex: number = this.inlineColumns.findIndex((c) => {
      if (c.formula?.includes(column.id)) {
        // Determine whether this column was chosen as text or link
        return (
          (column.isLink && isInlineColumnReadonlyLink(c)) ||
          (!column.isLink && !isInlineColumnReadonlyLink(c)) ||
          false
        );
      } else if (
        'originalName' in c &&
        'isLink' in c &&
        c.originalName === column.originalName &&
        c.isLink === column.isLink
      ) {
        return true;
      }
      return false;
    });
    if (selectedIndex > -1) {
      this.inlineColumns.splice(selectedIndex, 1);
    } else {
      this.inlineColumns = [...this.inlineColumns, column as Partial<DatasetColumn>];
    }
    this.updateColumn();
  }

  //#endregion

  hasExpandableLinks(dataset: BlazeTable): boolean {
    return this.blazeTableService.hasExpandableLinks(dataset?.id);
  }

  setPendingChanges(): void {
    let [columnA, columnB] = [this.updatedColumn, this.initialUpdatedColumn];
    // if we are editing a original link column and we replace tableName & columnName
    if (
      this.initialUpdatedColumn?.columnType === 'Link' &&
      !this.initialUpdatedColumn.formula &&
      columnA &&
      columnB
    ) {
      columnA = {
        ...columnA,
        link: {
          ...columnA.link,
          tableName: undefined,
          columnName: '',
          inlineDatasetColumns:
            (columnA.link as DatasetLinkExtendedForLinkEdit | undefined)?.inlineDatasetColumns ||
            [],
        } as DatasetLinkExtendedForLinkEdit,
      };
      columnB = {
        ...columnB,
        link: {
          ...columnB.link,
          tableName: undefined,
          columnName: '',
          inlineDatasetColumns:
            (columnB.link as DatasetLinkExtendedForLinkEdit | undefined)?.inlineDatasetColumns ||
            [],
        } as DatasetLinkExtendedForLinkEdit,
      };
      // if we are editing related to link column we care only about inlineDatasetColumns in link
    } else if (
      this.initialUpdatedColumn?.columnType === 'Link' &&
      this.initialUpdatedColumn.formula &&
      columnA &&
      columnB
    ) {
      columnA = {
        ...columnA,
        link: {
          inlineDatasetColumns:
            (columnA.link as DatasetLinkExtendedForLinkEdit | undefined)?.inlineDatasetColumns ||
            [],
        } as DatasetLinkExtendedForLinkEdit,
      };
      columnB = {
        ...columnB,
        link: {
          inlineDatasetColumns:
            (columnB.link as DatasetLinkExtendedForLinkEdit | undefined)?.inlineDatasetColumns ||
            [],
        } as DatasetLinkExtendedForLinkEdit,
      };
    }
    const isEqToSource = isEqualNoUndefined(columnA, columnB);
    this.blazeTableStore.setPendingChanges(!isEqToSource);
  }

  async showLinkedColumn(): Promise<void> {
    this.datasetsApiService
      .getAllDatasets(this.projectId)
      .pipe(withLatestFrom(this.projectsCollService.entities$))
      .pipe(
        map(([datasets, projects]) => {
          const tables = convertDatasetRefSchemasToBlazeTables(datasets);
          return { tables, projects };
        }),
      )
      .subscribe(({ tables, projects }) => {
        const fds = this.filterDatasets(tables, projects);
        this.datasets$$.next(fds);
        const column = this.getLinkColumnOrConnectedLinkColumn();
        if (column) {
          this.link = column.link || null;
          const linkedDataset = fds?.find((dataset) => dataset.id === this.link?.tableId);
          if (linkedDataset) {
            this.showColumnsOrSelectDefault(linkedDataset);
            if (this.isLinkToDisabled && this.linkedTableCtrl.enabled) {
              this.linkedTableCtrl.disable({ emitEvent: false });
            }
          }
        }
        this.updateColumn();
      });
  }

  @HostListener('keydown.esc', ['$event'])
  handleEscKey($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.close();
  }

  private setRollupConfigLinkedColumns(relatedDataset: RelatedDataset): void {
    this.rollupConfigColumnIds = relatedDataset.links
      .filter((l) => l.isReverseLink)
      .map((data) => ({
        label: data.columnName,
        value: data.columnId ?? '',
        data,
      }));
  }

  private setRollupConfigTableColumns(tableId = this.rollupDefinition?.tableId): void {
    if (!tableId) {
      this.rollupConfigTableColumns = [];
      return;
    }
    this.rollupConfigTableColumns =
      this.schemaService
        .getDatasetSchema(tableId)
        ?.columns.filter((col) => col.dataType === 'Number' && !col.isInternal)
        .map((data) => ({
          value: data.id as string,
          label: data.name,
          data,
        })) || [];
    const dataset = this.schemaService.getDatasetSchema(tableId);
    if (dataset) {
      this.rollupComplexFilterService.setup(dataset);
    }
    safeDetectChanges(this.cdr);
  }

  private showDatasets(): void {
    this.dataset = null;
    this.link = null;
    if (!this.datasets$$.value?.length) {
      this.datasetsApiService
        .getAllDatasets(this.projectId)
        .pipe(withLatestFrom(this.projectsCollService.entities$))
        .pipe(
          map(([datasets, projects]) => {
            const tables = convertDatasetRefSchemasToBlazeTables(datasets);
            return { tables, projects };
          }),
        )
        .subscribe(({ tables, projects }) => {
          this.datasets$$.next(this.filterDatasets(tables, projects));
          safeDetectChanges(this.cdr);
          return tables;
        });
    }
    this.updateColumn();
  }

  private showColumnsOrSelectDefault(
    dataset: BlazeTable,
    setDefaultValue = false,
    event?: MouseEvent,
  ): void {
    event?.stopPropagation();
    this.fieldDatasetMenuTrigger?.closeMenu();

    if (this.blazeTableService.hasExpandableLinks(dataset?.id)) {
      this.noValidColumnsFound = false;
      safeDetectChanges(this.cdr);
      this.dataset = dataset;
      if (this.isLinkToDisabled && this.linkedTableCtrl.enabled) {
        this.linkedTableCtrl.disable();
      }
      const column = dataset?.columns?.find(({ name }) => name === DEFAULT_ID_COL);
      this.datasetInlineColumns$$.next(dataset.id);
      if (column) {
        if (setDefaultValue) {
          this.defaultValue = null;
        }
        this.setLinkDatasetColumn(column);
      } else {
        this.notificationService.error(`Unable to find ${DEFAULT_ID_COL} column in dataset.`);
      }
      return;
    }

    this.noValidColumnsFound = false;
    safeDetectChanges(this.cdr);
    this.dataset = dataset;
    this.dataset.columns = this.filterValidColumnType(this.dataset.columns, this.sourceColumn);
    // No columns found. Fetch column data.
    if (!this.dataset.columns?.length) {
      this.datasetsCollService
        .getByKey(dataset.id)
        .pipe(take(1))
        .subscribe((datasetInfo: FullDatasetInfo) => {
          const columns = this.filterValidColumnType(datasetInfo.columns, this.sourceColumn);
          if (!columns?.length) {
            this.noValidColumnsFound = true;
          }
          if (this.dataset) {
            this.dataset.columns = this.blazeTableService
              .prepareColumns(this.dataset)
              .sort(compareAscBy('name'));
            /*
             * blaze tables has connectorName equal to null
             * and we are disabling possibility to select column for them
             */
            if (this.dataset.connectorName === null) {
              this.setLinkDatasetColumn(this.dataset.columns[0]);
            }
            safeDetectChanges(this.cdr);
          }
        });
    }
  }

  private filterDatasets(datasets: BlazeTable[], projects: ProjectInfo[]): BlazeTable[] {
    return datasets
      .filter((dataset) => dataset.connectorName === null && dataset.id !== this.tableId)
      .map((dataset) => {
        if (!dataset.name && dataset.projectId) {
          return {
            ...dataset,
            projectName: projects.find((project) => project.id === dataset.projectId)?.name || '',
          };
        }
        return dataset;
      });
  }

  private resetPanel(): void {
    this.dataset = null;
    this.datasets$$.next([]);
    this.displayInfos = [];
    this.fieldName = '';
    this.fieldType = undefined;
    this.formula = '';
    this.isMultipleValues = false;
    this.allowDynamicValues = false;
    this.isRequired = false;
    this.isUnique = false;
    this.defaultValue = null;
    this.link = null;
    this.tags = [];
    this.isSecure = false;
    const formulaFieldType = this.optionsFieldTypes().find((o) => o.value === 'Formula');
    if (formulaFieldType) {
      formulaFieldType.data.columnDataType = 'String';
    }
    this.rollupDefinition = {
      tableId: '',
      columnId: '',
      aggregateFunctionName: 'NONE',
      linkColumnId: '',
      query: {
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [],
      },
    };
    this.updateColumn();
  }

  private updateColumn(detectChanges = true): DatasetColumn {
    const column: DatasetColumn = {
      columnType: this.fieldType?.columnType,
      dataType: this.fieldType?.columnDataType,
      defaultValue: this.defaultValue,
      displayInfos: this.displayInfos,
      formula: this.formula,
      isRequired: this.isRequired,
      isSecure: this.isSecure,
      isProfilePicture: !!this.fieldType?.isProfilePicture,
      isUnique: this.isUnique && this.fieldType?.columnDataType !== 'Link',
      name: this.fieldName,
    };

    if (this.dateTimeFieldOption && column.columnType && this.isColumnTypeDate(column.columnType)) {
      column.columnType = this.dateTimeFieldOption;
      column.dataType = this.dateTimeFieldOption;

      if (!column.defaultValue) {
        column.defaultValue = undefined;
      }
    }

    if (this.fieldType?.columnDataType === 'Time') {
      if (column.defaultValue === 'None') {
        column.defaultValue = '';
      }
    }

    if (Array.isArray(this.defaultValue) && !this.defaultValue.length) {
      column.defaultValue = null;
    }

    if (this.fieldType?.columnDataType === 'String') {
      if (this.textFieldFormat) {
        column.format = {
          formatType: this.textFieldFormat,
          culture: this.formatCulture,
        };
      }
    }

    if (this.fieldType?.columnType === 'Email') {
      column.format = {
        formatType: 'Email',
      };
      column.columnType = 'String';
    }

    if (this.fieldType?.columnType === 'PhoneNumber') {
      const culture = getCultureByCountry(this.formatCulture);
      column.format = {
        formatType: 'PhoneNumber',
        culture,
      };
      column.columnType = 'String';
    }

    if (this.fieldType?.columnType === 'ZipCode') {
      const culture = getCultureByCountry(this.formatCulture);
      column.format = {
        formatType: 'ZipCode',
        culture,
      };
      column.columnType = 'String';
    }

    if (this.fieldType?.columnType === 'Url') {
      column.format = {
        formatType: 'Url',
      };
      column.columnType = 'String';
    }

    if (this.fieldType?.columnType === 'TimeZone') {
      column.format = {
        formatType: 'TimeZone',
      };
      column.columnType = 'String';
    }

    if (this.fieldType?.columnDataType === 'Duration') {
      column.format = {
        formatType: 'Duration',
        durationFormat: this.durationFieldFormat || 'h:mm',
      };
      if (column.columnType !== 'Formula') {
        column.columnType = 'String';
      }
    }

    if (this.fieldType?.columnDataType === 'Number') {
      if (this.numberFieldFormat) {
        column.format = {
          formatType: this.numberFieldFormat,
        };

        if (this.numberFieldFormat === 'Currency') {
          column.format.currencySymbol = this.currency;
        }
      }

      if (Number.isInteger(this.numberPrecision)) {
        column.precision = this.numberPrecision;
      }
    }

    if (this.link && this.fieldType?.columnDataType === 'Link') {
      column.multipleValues = this.isMultipleValues;
      (column as DatasetColumnExtendedForLinkEdit).link = {
        ...this.link,
        multipleValues: this.isMultipleValues,
        inlineDatasetColumns: findMatchingInlineColumns(
          this.datasetInlineColumns,
          this.inlineColumns,
        ),
      };
    }

    if (this.fieldType?.columnType === 'AccessRole') {
      column.columnType = 'Tag';
      column.dataType = 'Tag';
      column.isAccessRole = true;
      column.multipleValues = true;
    }

    if (this.fieldType?.columnType === 'ProfilePicture') {
      column.columnType = 'Image';
      column.dataType = 'Image';
      column.isComputed = true;
      column.isProfilePicture = true;
      column.isProtected = true;
      column.multipleValues = false;
      // name of the profile picture column is fixed to "ProfilePicture"
      column.name = 'ProfilePicture';
      this.fieldName = column.name;
    }

    if (this.fieldType?.columnDataType === 'Tag') {
      column.multipleValues = this.isMultipleValues;
      column.tagDefinition = {
        items: this.tags as TagItemInfo[],
        allowDynamicValues: this.allowDynamicValues,
      };
      if (Array.isArray(column.defaultValue) && (column.defaultValue as string[])) {
        column.defaultValue = column.defaultValue.filter((defaultTag) =>
          this.tags.find((tag) => tag.value === defaultTag),
        );
      } else if (!this.tags.some((tag) => tag.value === column.defaultValue)) {
        column.defaultValue = null;
      }
    }

    if (this.fieldType?.columnDataType === 'File') {
      column.multipleValues = this.isMultipleValues;
      column.isSecure = this.isSecure;
    }

    if (this.fieldType?.columnDataType === 'Image') {
      column.multipleValues = this.isMultipleValues;
    }

    if (
      (Array.isArray(this.defaultValue) && !this.defaultValue.length) ||
      (column.dataType === 'Tag' && !column.multipleValues && !this.defaultValue)
    ) {
      column.defaultValue = null;
    }

    if (this.fieldType?.columnDataType === 'Signature') {
      if (this.signatureFieldOption) {
        column.signatureFormat = { signatureType: this.signatureFieldOption };
      }
    }

    if (this.fieldType?.columnType === 'SecureText') {
      delete column.defaultValue;
      column.isUnique = false;
      column.columnType = 'String';
      column.format = {
        formatType: 'SecureText',
      };
    }

    if (this.fieldType?.columnType === 'Rollup' && this.rollupDefinition) {
      column.rollupDefinition = deepClone(this.rollupDefinition);
      if (!this.rollupFiltersEnabled) {
        column.rollupDefinition.query = null;
      }
    }

    if (this.fieldType?.columnType && !COLUMN_TYPES_WITH_FORMAT.has(this.fieldType.columnType)) {
      delete column.format;
    }

    this.updatedColumn = column;
    if (detectChanges) {
      safeDetectChanges(this.cdr);
    }

    this.setPendingChanges();
    return column;
  }

  private createIntelliSenseConfig$(): Observable<IntelliConfig> {
    return combineLatest({
      fieldType: this.fieldType$$.asObservable(),
      sourceColumn: this.blazeTableStore.sourceColumn$,
      tableConfig: this.tableConfig$,
      formulaError: this.formulaError$$,
    }).pipe(
      filter(
        ({ fieldType, sourceColumn, tableConfig }) =>
          !!fieldType && !!sourceColumn && !!tableConfig,
      ),
      map(({ fieldType, sourceColumn, tableConfig, formulaError }) => {
        let updatedColumn: DatasetColumn | null = sourceColumn;
        if (this.sourceColumn) {
          updatedColumn = {
            ...sourceColumn,
            columnType: fieldType?.columnType,
            dataType: fieldType?.columnDataType,
            isProfilePicture: !!fieldType?.isProfilePicture,
            name: 'columnEditorPanelConfigId',
          };
        }
        const errors: IntellisenseError[] = formulaError
          ? [new InvalidFormulaIntellisenseError(formulaError)]
          : [];
        const width = this.host.nativeElement?.offsetWidth;
        const config: IntelliConfig = {
          autocompleteWidth: width ? width - 34 : undefined,
          column: updatedColumn,
          datasetSchema: tableConfig,
          id: `columnEditorPanelConfigId`,
          inputSchema: this.createSchemaFromBlazeTable(tableConfig),
          isBlazeTableFormulaColumn: true,
          pageContextSchema: null, // no Page Context in Blaze Table
          valueInfo: {
            displayInfos: this.displayInfos,
            value: this.formula,
          },
          errors,
        };
        return config;
      }),
    );
  }

  private createSchemaFromBlazeTable(config: BlazeTable | undefined): BaseSchema | undefined {
    if (!config && this.tableId) {
      return this.schemaService.getSchema(this.tableId);
    } else if (!config) {
      return DEFAULT_WF_SCHEMA;
    }
    const schema: BaseSchema = {
      columns: config.columns,
      id: config.id,
      name: config.name,
    };
    return schema;
  }

  /**
   * Filters columns based on the source column data type (or source column link data type).
   * @param columns - List of columns to filter.
   * @param column - Source column
   * @returns - List of columns which match given datatype.
   */
  private filterValidColumnType(
    columns?: DatasetColumn[],
    column?: DatasetColumn | null,
  ): DatasetColumn[] {
    const columnDataType = column?.dataType !== 'Link' ? column?.dataType : column.link?.dataType;
    let filteredColumns: DatasetColumn[] = columns || [];

    // Filter out only columns with invalid data type.
    if (this.actionType !== BlazeTableActionType.Edit) {
      return filteredColumns.filter(({ dataType }) => {
        const isInvalidDataType =
          !dataType || !FIELD_TYPES[dataType || ''] || dataType === 'Boolean';
        return !isInvalidDataType;
      });
    }
    // Filter out invalid data types and columns
    if (column?.dataType && columns?.length) {
      filteredColumns = columns.filter(({ dataType }) => {
        const isInvalidDataType =
          !dataType || !FIELD_TYPES[dataType || ''] || dataType === 'Boolean';
        return dataType === columnDataType && !isInvalidDataType ? true : false;
      });
    }
    return filteredColumns;
  }

  /**
   * Set defaults for adding new column. All types of fields are allowed.
   * Important: do not sort alphabetically. Order of assignments matter.
   */
  private setDefaultsForActionAdd(): void {
    this.setFieldTypes();
    this.fieldType = undefined;
    this.currency = 'USD';
    this.numberFieldFormats = NUMBER_FIELD_FORMATS;
    this.numberFieldFormat = null;
    this.textFieldFormats = TEXT_FIELD_FORMATS;
    this.textFieldFormat = this.textFieldFormats[0].value;
    this.durationFieldFormats = DURATION_FIELD_FORMATS;
    this.durationFieldFormat = this.durationFieldFormats[0].value;
    this.signatureFieldOptions = SIGNATURE_FIELD_OPTIONS;
    this.signatureFieldOption = this.signatureFieldOptions[0].value;
    this.dateTimeFieldOptions = DATETIME_FIELD_FORMATS;
    this.dateTimeFieldOption = this.dateTimeFieldOptions[0].value;
    this.dateTimeDefaultValue = '';
    this.numberDefaultValue = null;
    this.fieldName = '';
    this.formula = '';
    this.displayInfos = [];
    this.isMultipleValues = false;
    this.allowDynamicValues = false;
    this.isRequired = false;
    this.isSecure = false;
    this.isUnique = false;
    this.defaultValue = null;
    this.numberPrecision = this.numberPrecisions[0].value;
    this.tags = [];
  }

  /**
   * Sets defaults (available field types, title, etc) for Edit action.
   * There are only two valid options for changing type of the field:
   * 1. Change from a Link to the type we link to (ie. "link" points to a Number field, we can change type to "Number")
   * 2. Change from the current type to the Link type that points towards the same type we currently have.
   */
  private setDefaultsForActionEdit(sourceColumn: DatasetColumn | null): void {
    if (sourceColumn?.columnType) {
      // Set available field types based on the source column data type.
      this.setFieldTypes();

      // Set defaults
      this.numberFieldFormats = Object.values(NUMBER_FIELD_FORMATS);
      this.textFieldFormats = Object.values(TEXT_FIELD_FORMATS);
      this.durationFieldFormats = Object.values(DURATION_FIELD_FORMATS);
      this.signatureFieldOptions = SIGNATURE_FIELD_OPTIONS;
      this.dateTimeFieldOptions = Object.values(DATETIME_FIELD_FORMATS);
      this.currency = sourceColumn.format?.currencySymbol || 'USD';
      this.displayInfos = sourceColumn?.displayInfos || [];
      if (sourceColumn?.rollupDefinition) {
        this.rollupDefinition = sourceColumn?.rollupDefinition;
        this.rollupConfigTableId$.pipe(first()).subscribe((tables) => {
          this.onRollupSourceTableChange(this.rollupDefinition.tableId, tables);
          this.onRollupSourceColumnChange(
            this.rollupDefinition.linkColumnId,
            this.rollupConfigColumnIds,
          );
        });
        this.rollupFiltersEnabled = !!sourceColumn.rollupDefinition.query?.children?.length;
        if (!this.rollupFiltersEnabled) {
          this.rollupDefinition.query = {
            nodeType: QueryNodeType.Logical,
            logicalType: LogicalType.And,
            children: [],
          } satisfies LogicalQueryNode;
        }
      }
      this.fieldType = this.getColumnFieldType(sourceColumn);
      this.numberFieldFormat =
        this.numberFieldFormats.find((fFormat) => fFormat.value === sourceColumn.format?.formatType)
          ?.value || null;
      this.textFieldFormat = getValueFromOptions(
        this.textFieldFormats,
        sourceColumn.format?.formatType,
      );
      this.durationFieldFormat = getValueFromOptions(
        this.durationFieldFormats,
        sourceColumn.format?.durationFormat,
        DEPRECATED_DURATION_FORMAT_MAPPINGS,
      );
      this.signatureFieldOption = getValueFromOptions(
        this.signatureFieldOptions,
        sourceColumn.signatureFormat?.signatureType,
      );
      this.dateTimeFieldOption = getValueFromOptions(
        this.dateTimeFieldOptions,
        sourceColumn.columnType,
      );
      this.dateTimeDefaultValue = this.dateTimeDefaultValues[0].value;
      this.numberDefaultValue = sourceColumn.defaultValue === 'NONE' ? 'NONE' : null;
      this.isSecure = sourceColumn.isSecure ?? false;
      if (sourceColumn?.columnType !== 'Formula') {
        if (this.isColumnTypeStringWithFormat(sourceColumn.format?.formatType)) {
          this.fieldType = this.fieldTypes.find(
            (fType) => fType?.columnType === sourceColumn.format?.formatType,
          );
        } else if (this.isColumnTypeDate(sourceColumn.columnType)) {
          this.fieldType = FIELD_TYPES.DateTime;
          this.dateTimeFieldOption = sourceColumn.columnType as DateTimeColumnFormat;
          if (sourceColumn.columnType === 'Date') {
            this.dateTimeDefaultValue =
              this.dateDefaultValues.find((dValue) => dValue.value === sourceColumn.defaultValue)
                ?.value || this.dateDefaultValues[0].value;
          } else {
            this.dateTimeDefaultValue =
              this.dateTimeDefaultValues.find(
                (dValue) => dValue.value === sourceColumn.defaultValue,
              )?.value || this.dateTimeDefaultValues[0].value;
          }
        } else if (this.isColumnSecureText(sourceColumn)) {
          this.fieldType = FIELD_TYPES.SecureText;
        }
      }
      this.fieldTypeCtrl.setValue(this.fieldType?.viewValue || null, { emitEvent: false });
      if (sourceColumn.format?.culture) {
        this.formatCulture = sourceColumn.format.culture;
      }
      this.fieldName = sourceColumn?.name || '';
      this.formula = sourceColumn?.formula || '';
      this.isMultipleValues =
        (sourceColumn.columnType === 'File' && sourceColumn?.multipleValues) ||
        (sourceColumn.columnType === 'Image' && sourceColumn?.multipleValues) ||
        (sourceColumn.dataType === 'Tag' && sourceColumn?.multipleValues) ||
        (sourceColumn.dataType === 'Link' && sourceColumn.link?.multipleValues) ||
        false;
      this.allowDynamicValues =
        (sourceColumn.columnType === 'Tag' && sourceColumn?.tagDefinition?.allowDynamicValues) ||
        false;
      this.isRequired = sourceColumn?.isRequired || false;
      this.isSecure = sourceColumn?.isSecure || false;
      this.isUnique = sourceColumn?.isUnique || false;
      this.defaultValue = sourceColumn?.defaultValue;
      this.numberPrecision = (
        this.numberPrecisions.find((nPrecision) => nPrecision.value === sourceColumn.precision) ||
        this.numberPrecisions[0]
      ).value;
      this.tags = sourceColumn?.tagDefinition?.items || [];
      this.title = 'Edit Field Type';
      this.saveButtonLabel = 'Save';
    }
  }

  private isColumnTypeDate(columnType = this.sourceColumn?.columnType): boolean {
    return ['Date', 'DateTime'].includes(columnType || '');
  }

  private isColumnTypeStringWithFormat(
    formatType = this.sourceColumn?.format?.formatType,
  ): boolean {
    return ['Email', 'Url', 'PhoneNumber', 'TimeZone', 'ZipCode', 'Duration'].includes(
      formatType || '',
    );
  }

  private isColumnSecureText(column = this.sourceColumn): boolean {
    return (column?.isSecure && column.format?.formatType === 'SecureText') ?? false;
  }

  private getLinkColumnOrConnectedLinkColumn(column = this.sourceColumn): DatasetColumn | null {
    return column?.columnType === 'Link'
      ? column
      : column?.columnType === 'Formula' && isLinkedInlineColumn(column)
        ? this.tableConfig.columns.find((c) => c.id && column.formula?.includes(c.id)) || null
        : null;
  }

  private getColumnFieldType(column: DatasetColumn): FieldTypeOption {
    return (
      this.fieldTypes.find(
        (fType) =>
          (fType?.columnType === column?.columnType && !column.isAccessRole) ||
          fType.columnType === 'AccessRole',
      ) ?? this.fieldTypes[0]
    );
  }

  private setFieldTypes(): void {
    const fieldType = this.getSourceColumnColumnType();
    if (this.actionType === BlazeTableActionType.Edit) {
      if (!fieldType) {
        this.fieldTypes = [];
      }

      const isColTypeFormula = this.sourceColumn?.columnType === 'Formula';
      this.fieldTypes = Object.entries(FIELD_TYPES)
        .filter(([colType, _typeOption]) => {
          return (
            (this.isLinkEditView && colType === 'Link') ||
            colType === fieldType ||
            (isColTypeFormula && colType === 'Formula') ||
            this.allowedConversions[fieldType as ColumnType]?.includes(colType as ColumnType)
          );
        })
        .map(([_, typeOption]) => typeOption);
    } else {
      this.fieldTypes = Object.values(FIELD_TYPES).filter((c) => {
        if (isAccessRoleColumn(c)) {
          return this.canAddAccessRoleColumn;
        } else if (isProfilePictureColumn(c)) {
          return this.canAddProfilePictureColumn;
        }
        return true;
      });
    }

    // Profile picture colunn is an internal column that's not available for adding.
    if (this.tableConfig?.systemTableType === SystemTableType.Users) {
      this.fieldTypes = this.fieldTypes.filter((ft) => !ft.isProfilePicture);
    }

    this.optionsFieldTypes.set(
      this.fieldTypes.map<SelectOptionWithData<FieldTypeOption>>((ft) => ({
        value: ft.columnType,
        label: ft.viewValue,
        data: ft,
      })),
    );
  }

  private getSourceColumnColumnType(): ColumnType | undefined {
    const fieldTypesEntries = Object.entries(FIELD_TYPES);
    let key: ColumnType | undefined = undefined;
    const formatType = this.sourceColumn?.format?.formatType;
    const columnType = this.sourceColumn?.columnType;
    if (formatType && this.isColumnTypeStringWithFormat(formatType)) {
      key = fieldTypesEntries.find(
        ([_, fType]) => fType?.columnType === formatType,
      )?.[0] as ColumnType;
    } else if (columnType && this.isColumnTypeDate(columnType)) {
      key = 'DateTime';
    } else if (columnType === 'Formula') {
      if (this.sourceColumn?.dataType === 'Array') {
        key = 'String';
      } else {
        key =
          (Object.keys(FIELD_TYPES).find(
            (type) => type === this.sourceColumn?.dataType,
          ) as ColumnType) ?? 'Formula';
      }
    } else if (this.isColumnSecureText()) {
      key = 'SecureText';
    } else if (isAccessRoleColumn(this.sourceColumn)) {
      key = 'AccessRole';
    } else if (isProfilePictureColumn(this.sourceColumn)) {
      key = 'ProfilePicture';
    } else {
      key = fieldTypesEntries.find(
        ([_, fieldTypeOptions]) => fieldTypeOptions.columnType === columnType,
      )?.[0] as ColumnType;
    }
    return key;
  }

  private validateFormula(column: DatasetColumn, tableId: string): Observable<DatasetColumn> {
    if (column.formula?.includes('FLOOR') && !regexpMatch(formulaFloorRegex, column.formula)) {
      return throwError(() => new Error('The FLOOR function requires two parameters.'));
    } else {
      return this.datasetsApiService.verifyColumn(tableId, column);
    }
  }

  private verifyColumn(column: DatasetColumn | null): void {
    if (this.tableId && column?.columnType === 'Formula') {
      this.validateFormula(column, this.tableId)
        .pipe(
          catchError(() => of({ dataType: 'String' as ColumnDataType })),
          finalize(() => safeDetectChanges(this.cdr)),
        )
        .subscribe((res) => {
          this.fieldType = deepClone(this.fieldType);
          if (this.fieldType) {
            this.fieldType.columnDataType = res.dataType;
          }
          if (this.updatedColumn) {
            this.updatedColumn.dataType = res.dataType;
          }
        });
    }
  }

  /**
   * Checks whether table associated with the linked column is also linked in the project itself.
   */
  private verifySourceColumnTableLinked(datasets: BlazeTable[]): void {
    if (this.dataset) {
      this.linkedTableCtrl.markAsPristine();
      this.linkedTableCtrl.setErrors(null);
      return;
    }
    if (!this.tableConfig) {
      return;
    }
    const sourceColumnTable = datasets?.some(
      ({ id }) => !!id && id === this.sourceColumn?.link?.tableId,
    );
    if (this.sourceColumn?.link?.tableId && !sourceColumnTable) {
      this.linkedTableCtrl.markAsDirty();
      this.linkedTableCtrl.markAsTouched();
      this.linkedTableCtrl.setErrors({ missingLinkedSourceTable: true });
    }
    if (this.isLinkToDisabled && this.linkedTableCtrl.enabled) {
      this.linkedTableCtrl.disable({ emitEvent: false });
    }
  }

  private fixLinkedColumnTypes(column: DatasetColumn): DatasetColumn {
    if (
      this.actionType !== 'Edit' ||
      !this.sourceColumn ||
      !(this.sourceColumn.columnType === 'Formula' && column.columnType === 'Link')
    ) {
      return column;
    }
    const result = {
      ...column,
      dataType: this.sourceColumn.dataType,
      columnType: 'Formula',
    } satisfies DatasetColumn;

    return result;
  }
}

function wasMultipleValue(sourceColumn: Nullish<DatasetColumn>): boolean {
  if (!sourceColumn) {
    return false;
  }

  if (sourceColumn.dataType === 'Link') {
    return (
      sourceColumn.link?.multipleValues !== undefined && sourceColumn.link.multipleValues === true
    );
  } else if (
    sourceColumn.dataType === 'Tag' ||
    sourceColumn.columnType === 'File' ||
    sourceColumn.columnType === 'Image'
  ) {
    return sourceColumn.multipleValues === true;
  }

  return false;
}

function getValueFromOptions<D extends string, O extends D>(
  options: SelectOption<O | null>[],
  desiredValue: Nullish<D>,
  deprecationMappings?: { [key in D]?: O },
): O | null {
  const foundInOptions = options.find((option) => option.value === desiredValue);
  if (foundInOptions) {
    return foundInOptions.value;
  }

  if (deprecationMappings && desiredValue) {
    const foundDeprecationMapping = deprecationMappings[desiredValue];
    if (foundDeprecationMapping) {
      return foundDeprecationMapping;
    }
  }

  return options[0].value;
}

function isAccessRoleColumn(
  c: {
    columnType: ColumnType | undefined;
    isAccessRole?: boolean;
  } | null,
): boolean {
  if (!c) {
    return false;
  }
  return c.columnType === 'AccessRole' || (c.columnType === 'Tag' && c.isAccessRole === true);
}

function isProfilePictureColumn(c: { isProfilePicture?: boolean } | null): boolean {
  return c?.isProfilePicture === true;
}
